import { useMemo } from 'react'
import LanguageOption from '../../components/LanguageOption'
import { Locale, useLang } from '../../components/Locale'
import { useGet } from '../../hooks/useGet'
import {
  getSubscriptionPlans,
  GetSubscriptionPlansOutput,
  subscribeCredit,
  cancelSubscription,
  resumeSubscription,
  changeSubscriptionPlan,
} from '../../sdk/credit'
import './Subscription.css'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToken } from '../../hooks/useToken'
import { usePageLayout } from '../../hooks/usePageLayout'

type Plan = Pick<
  GetSubscriptionPlansOutput['plans'][number],
  'id' | 'title_en' | 'title_zh_cn' | 'title_zh_hk'
>

type Subscription =
  GetSubscriptionPlansOutput['plans'][number]['subscriptions'][number]

export default function Subscription() {
  // FIXME: replace localhost with actual domain

  // Memoize layout options to prevent infinite re-renders
  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Subscription Plans',
        zh_cn: '订阅方案',
        zh_hk: '訂閱方案',
      },
    }),
    [],
  )

  // Subscription page should show all navigation elements
  usePageLayout(layoutOptions)

  const { token } = useToken()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const auto_subscribe_plan_id = +params.get('plan')!

  const handleChangeSubscription = async (plan: Plan) => {
    try {
      const json = await changeSubscriptionPlan({
        params: {
          plan_id: plan.id,
          subscription_id: currentSubscription[0]?.id,
        },
      })
      // redirect to payment page
      window.location.href = json.url
    } catch (error) {
      console.error(error)
    }
  }

  const [cancelling, setCancelling] = useState<{
    plan: Plan
    subscription: Subscription
  } | null>(null)
  const [isLoadingCancel, setIsLoadingCancel] = useState(false)

  const submitCancelSubscription = async (options: {
    plan: Plan
    subscription: Subscription
  }) => {
    try {
      setIsLoadingCancel(true)
      await cancelSubscription({
        params: { subscription_id: options.subscription.id },
      })
      subscribePlans.reload()
      setCancelling(null)
      window.location.reload()
    } catch (error) {
      // TODO show error
      console.error(error)
    } finally {
      setIsLoadingCancel(false)
    }
  }

  const handleResume = async (subscription: Subscription) => {
    try {
      await resumeSubscription({
        params: { subscription_id: subscription.id },
      })
      subscribePlans.reload()
      window.location.reload()
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (auto_subscribe_plan_id) {
      requestSubscribe(auto_subscribe_plan_id)
    }
  }, [auto_subscribe_plan_id])

  async function requestSubscribe(plan_id: number) {
    if (!token) {
      navigate(
        `/login?` +
          new URLSearchParams({
            page: `subscription`,
            plan: plan_id.toString(),
          }).toString(),
      )
      return
    }
    let json = await subscribeCredit({
      body: {
        plan_id,
      },
    })
    // redirect to the payment page
    window.location.href = json.url
  }

  const subscribePlans = useGet({ plans: [] }, () => getSubscriptionPlans({}))

  const subscribingPlans: Plan[] = subscribePlans.state.plans.filter(
    plan => plan.subscriptions.length > 0,
  )
  const currentSubscription: Subscription[] = subscribePlans.state.plans
    .flatMap(plan => plan.subscriptions)
    .filter(subscription => subscription.id > 0)
  const isSubscribing = subscribingPlans.length > 0

  const freePlan = {
    id: 0,
    title_en: 'Free Plan',
    title_zh_cn: '免费方案',
    title_zh_hk: '免費方案',
  }

  // Get the current locale from the useLang hook
  const { lang: locale } = useLang()

  // Get current and next plan titles for comparison
  const currentPlanTitle =
    currentSubscription.length > 0
      ? (() => {
          const plan = subscribePlans.state.plans.find(
            p =>
              p.id === currentSubscription[0]?.effective_subscription_plan_id,
          )
          if (!plan) return ''
          if (locale === 'en') return plan.title_en
          if (locale === 'zh_cn') return plan.title_zh_cn
          if (locale === 'zh_hk') return plan.title_zh_hk
          return plan.title_en
        })()
      : (() => {
          if (locale === 'en') return freePlan.title_en
          if (locale === 'zh_cn') return freePlan.title_zh_cn
          if (locale === 'zh_hk') return freePlan.title_zh_hk
          return freePlan.title_en
        })()

  const nextPlanTitle =
    subscribingPlans.length > 0
      ? (() => {
          const plan = subscribingPlans[0]
          if (!plan) return ''
          if (locale === 'en') return plan.title_en
          if (locale === 'zh_cn') return plan.title_zh_cn
          if (locale === 'zh_hk') return plan.title_zh_hk
          return plan.title_en
        })()
      : ''

  return (
    <div className="Subscription">
      <h2>
        <Locale
          en="Choose Your Subscription Plan"
          zh_cn="选择您的订阅项目"
          zh_hk="選擇您的訂閱項目"
        />
      </h2>
      {token && (
        <>
          <h2>
            <Locale
              en="Your Current Subscription Plan: "
              zh_cn="您当前的订阅项目: "
              zh_hk="您目前的訂閱項目: "
            />
            {/* Show the plan name */}
            {currentSubscription.length > 0 ? (
              currentSubscription.map(subscription => {
                const plan = subscribePlans.state.plans.find(
                  p => p.id === subscription.effective_subscription_plan_id,
                )
                return (
                  <span key={subscription.effective_subscription_plan_id}>
                    <Locale
                      en={plan?.title_en || ''}
                      zh_cn={plan?.title_zh_cn || ''}
                      zh_hk={plan?.title_zh_hk || ''}
                    />
                  </span>
                )
              })
            ) : (
              <span>
                <Locale
                  en={freePlan.title_en}
                  zh_cn={freePlan.title_zh_cn}
                  zh_hk={freePlan.title_zh_hk}
                />
              </span>
            )}
          </h2>
          {/* Only show next month plan if it's different from current and user has a next plan */}
          {nextPlanTitle && currentPlanTitle !== nextPlanTitle && (
            <h2>
              <Locale
                en="Your Subscription Plan in Next Month: "
                zh_cn="您下个月的订阅项目: "
                zh_hk="您下個月的訂閱項目: "
              />
              {/* Show the plan name */}
              {subscribingPlans.map(plan => (
                <span key={plan.id}>
                  <Locale
                    en={plan.title_en}
                    zh_cn={plan.title_zh_cn}
                    zh_hk={plan.title_zh_hk}
                  />
                </span>
              ))}
            </h2>
          )}
        </>
      )}
      <h2>
        {/* TODO use sweetalert instead */}
        {/* Custom confirmation modal */}
        {cancelling && (
          <div className="custom-modal-overlay">
            <div className="custom-modal">
              <p>
                <Locale
                  en="Are you sure you want to unsubscribe?"
                  zh_cn={`您确定要取消${cancelling.plan.title_zh_cn}吗？`}
                  zh_hk={`您確定要取消${cancelling.plan.title_zh_hk}嗎？`}
                />
              </p>
              <div className="modal-buttons">
                {isLoadingCancel ? (
                  <button disabled>Load...</button>
                ) : (
                  <>
                    <button
                      className="primary-button yes-button"
                      onClick={() => submitCancelSubscription(cancelling)}
                    >
                      <Locale en="Yes" zh_cn="是" zh_hk="是" />
                    </button>
                    <button
                      className="primary-button no-button"
                      onClick={() => setCancelling(null)}
                    >
                      <Locale en="No" zh_cn="否" zh_hk="否" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </h2>
      <div className="subscription-container">
        {subscribePlans.state.plans?.map(plan => {
          let unitPrice = (plan.hkd / plan.credits).toFixed(2)

          // Find the user's subscription for this plan, if any
          const userSubscription = plan.subscriptions[0] // assuming only one per plan per user

          // Determine button condition
          let buttonType: 'subscribe' | 'change' | 'unsubscribe' | 'resume'
          if (!userSubscription) {
            buttonType = isSubscribing ? 'change' : 'subscribe'
          } else if (userSubscription.cancel_at === null) {
            buttonType = 'unsubscribe'
          } else if (userSubscription.cancel_at !== null) {
            buttonType = 'resume'
          } else {
            buttonType = 'subscribe'
          }
          // Check if this plan is the user's active subscription
          return (
            <div key={plan.id} className="subscription-plan">
              <h3 className="subscription-plan-title">
                <Locale
                  en={plan.title_en}
                  zh_cn={plan.title_zh_cn}
                  zh_hk={plan.title_zh_hk}
                />
              </h3>
              {/* Button logic */}
              {buttonType === 'subscribe' && (
                <button
                  onClick={() => requestSubscribe(plan.id)}
                  className="action-button"
                >
                  <Locale en="Subscribe" zh_cn="订阅" zh_hk="訂閱" />
                </button>
              )}
              {buttonType === 'change' && (
                <button
                  onClick={() => handleChangeSubscription(plan)}
                  className="action-button"
                >
                  <Locale en="Change Plan" zh_cn="更换方案" zh_hk="更換方案" />
                </button>
              )}
              {buttonType === 'unsubscribe' && (
                <button
                  onClick={() =>
                    setCancelling({ plan, subscription: userSubscription })
                  }
                  className="action-button"
                >
                  <Locale en="Unsubscribe" zh_cn="取消订阅" zh_hk="取消訂閱" />
                </button>
              )}
              {buttonType === 'resume' && (
                <button
                  onClick={() => handleResume(userSubscription)}
                  className="action-button"
                >
                  <Locale
                    en="Resume Subscription"
                    zh_cn="恢复订阅"
                    zh_hk="恢復訂閱"
                  />
                </button>
              )}
              <div className="subscription-plan-fee">
                <div>
                  <Locale
                    en={`${plan.hkd} for ${plan.credits} credits/month`}
                    zh_cn={`每个月${plan.hkd} 元充值 ${plan.credits} 点数`}
                    zh_hk={`每個月${plan.hkd} 元充值 ${plan.credits} 點數`}
                  />
                </div>
                <div>
                  (
                  <Locale
                    en={`${unitPrice} per credit`}
                    zh_cn={`${unitPrice} 元/点`}
                    zh_hk={`${unitPrice} 元/點`}
                  />
                  )
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
